<script setup>
  import useVuelidate from '@vuelidate/core'

  import { computed } from 'vue'

  import FieldRendererLabel from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererLabel.vue'
  import FieldRendererValidationMessages from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererValidationMessages.vue'
  import FieldRendererWrapper from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererWrapper.vue'
  import { useFieldPlugins } from '@/features/settingsManagement/fieldManagement/edit/plugin/fieldsPlugin.js'
  import {
    fieldCommonProps,
    normalizeFieldExtraData,
  } from '@/features/settingsManagement/fieldManagement/edit/utils/fieldUtils.js'
  import { mergeDeep } from '@/features/utils/object.utils.js'

  defineEmits(['update:modelValue'])

  const props = defineProps({
    ...fieldCommonProps,
  })

  const { getPlugin } = useFieldPlugins()
  const plugin = getPlugin('datetime-picker')

  const extraData = computed(() => {
    return mergeDeep(plugin.defaultExtraData, normalizeFieldExtraData(props.field.extraData))
  })

  const placeholder = computed(() => {
    return extraData.value.placeholder || ''
  })

  const hourFormat = computed(() => {
    return extraData.value.hourFormat
  })

  const showTime = computed(() => {
    return extraData.value.showTime
  })

  const datePickerView = computed(() => {
    return extraData.value.datePickerView
  })

  const timeOnly = computed(() => {
    return extraData.value.timeOnly
  })

  const rules = computed(() => ({
    value: plugin.getValidations({ props }),
  }))

  const state = computed(() => ({
    value: props.modelValue,
  }))

  const v$ = useVuelidate(rules, state)

  async function validate() {
    const result = await v$.value.$validate()
    return {
      isValid: result,
      errors: Object.entries(v$.value.$errors).map(([, error]) => error.$message),
    }
  }

  defineExpose({
    validate,
  })

  const modelValueToDate = computed({
    get: () => {
      if (!props.modelValue) {
        return null
      }

      return new Date(props.modelValue)
    },
    set: value => {
      return value.toISOString()
    },
  })
</script>

<template>
  <FieldRendererWrapper :field="field">
    <template #label>
      <FieldRendererLabel :field="field" :external-required="externalRequired" />
    </template>

    <DatePicker
      v-model="modelValueToDate"
      :placeholder="placeholder"
      :view="datePickerView"
      :hour-format="hourFormat"
      :show-time="showTime"
      :time-only="timeOnly"
      :disabled="disabled"
      :invalid="v$.value.$error || hasExternalError"
      @value-change="$emit('update:modelValue', $event)"
    />

    <template #validation>
      <FieldRendererValidationMessages v-if="v$.value.$error" :error-messages="v$.value.$errors" />
    </template>
  </FieldRendererWrapper>
</template>
